import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { CreateOrUpdateUserAsAdmin } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdmin";
import {
  getSelectedProjectGroup,
  fetchSpecificProjectGroupWithProjects,
} from "src/features/projectGroups/state/projectGroupsSlice";
import type { IUserRole } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdmin";
import { fetchUserDetails, getCreateOrEditUserDraft } from "src/features/team/state/teamSlice";
import Spinner from "src/common/components/Loader/Spinner";
import { CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdminPermissionsList";

/**
 * Story 19.6: Edit learner permissions scoped to a project-group
 * Wraps CreateOrUpdateUserAsAdmin in update mode with project-group filtering
 */
const ProjectGroupLearnersUpdate = () => {
  const { id: projectGroupIdStr, learnerId: learnerIdStr } = useParams<{
    id: string;
    learnerId: string;
  }>();
  const dispatch = useAppDispatch();
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);
  const userDetails = useAppSelector(getCreateOrEditUserDraft);

  const projectGroupId = projectGroupIdStr ? Number(projectGroupIdStr) : null;
  const learnerId = learnerIdStr ? Number(learnerIdStr) : null;

  useEffect(() => {
    if (projectGroupId && !isNaN(projectGroupId)) {
      void dispatch(fetchSpecificProjectGroupWithProjects({ projectGroupId }));
    }
  }, [dispatch, projectGroupId]);

  useEffect(() => {
    if (learnerId && !isNaN(learnerId)) {
      void dispatch(
        fetchUserDetails({
          id: learnerId,
          componentId: CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT,
        }),
      );
    }
  }, [dispatch, learnerId]);

  if (!projectGroupId || isNaN(projectGroupId)) {
    return <div data-testid="error-invalid-id">Invalid project group ID</div>;
  }
  if (!learnerId || isNaN(learnerId)) {
    return <div data-testid="error-invalid-learner-id">Invalid learner ID</div>;
  }

  const isCorrectProjectGroupLoaded =
    selectedProjectGroup &&
    selectedProjectGroup.auth_type &&
    Number(selectedProjectGroup.id) === projectGroupId;

  const isUserDetailsLoaded = userDetails && userDetails.id === learnerId;

  if (!isCorrectProjectGroupLoaded || !isUserDetailsLoaded) {
    return (
      <div data-testid="loading-spinner">
        <Spinner />
      </div>
    );
  }

  const userRole: IUserRole = (() => {
    const authType = selectedProjectGroup.auth_type;
    switch (authType) {
      case "simplified":
        return "simplifiedEnduser";
      case "classic":
      case "authenticated":
      default:
        return "enduser";
    }
  })();

  return (
    <div data-testid="project-group-learners-update" className="h-full">
      <CreateOrUpdateUserAsAdmin
        mode="update"
        userRole={userRole}
        projectGroupId={projectGroupId}
      />
    </div>
  );
};

export default ProjectGroupLearnersUpdate;
